import { useRef, useState } from "react";
import { brandPresets, brandWarnings, fontOptions, resolveBrand, type Brand } from "../lib/brand";
import { Field } from "./ui";

// BrandEditor edits a company's (or a template's) look and shows it live on a
// small booking card and chat bubble.
export function BrandEditor({ value, onChange, uploadLogo, logoURL, disabled = false }: {
  value: Brand;
  onChange: (b: Brand) => void;
  uploadLogo: (file: File) => Promise<string>;
  logoURL: (hash: string) => string;
  disabled?: boolean;
}) {
  const b = value;
  const set = (patch: Partial<Brand>) => onChange({ ...b, ...patch });
  const r = resolveBrand(b);
  const warnings = brandWarnings(b);
  const [uploading, setUploading] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const color = (key: "primary" | "background" | "surface" | "text", label: string) => {
    const v = r[key];
    return (
      <Field label={label}>
        <span className="color-field">
          <input type="color" aria-label={`${label} picker`} value={v} disabled={disabled} onChange={(e) => set({ [key]: e.target.value })} />
          <input value={b[key] ?? ""} placeholder={String(v)} maxLength={7} disabled={disabled} onChange={(e) => set({ [key]: e.target.value.trim() })} />
        </span>
      </Field>
    );
  };

  return (
    <div className="brand-editor">
      <div className="brand-form">
        <div className="field">
          <span>Start from</span>
          <div className="preset-row">
            {brandPresets.map((p) => {
              const pr = resolveBrand(p.brand);
              return (
                <button key={p.name} type="button" className="preset" disabled={disabled} title={p.name}
                  onClick={() => onChange({ ...b, ...p.brand })}>
                  <span style={{ background: pr.background }}><i style={{ background: pr.primary }} /><i style={{ background: pr.surface }} /></span>
                  {p.name}
                </button>
              );
            })}
          </div>
        </div>
        <div className="row">
          {color("primary", "Main color (buttons)")}
          {color("background", "Background")}
          {color("surface", "Panels")}
          {color("text", "Text")}
        </div>
        <div className="row">
          <Field label="Font">
            <select value={b.font ?? "system"} disabled={disabled} onChange={(e) => set({ font: e.target.value })}>
              {fontOptions.map(([k, name]) => <option key={k} value={k}>{name}</option>)}
            </select>
          </Field>
          <Field label="Corners">
            <select value={b.corners ?? "soft"} disabled={disabled} onChange={(e) => set({ corners: e.target.value as Brand["corners"] })}>
              <option value="sharp">Sharp</option><option value="soft">Soft</option><option value="round">Round</option>
            </select>
          </Field>
          <div className="field">
            <span>Logo (PNG or JPEG)</span>
            <div className="inline">
              {b.logo && <img className="logo-thumb" src={logoURL(b.logo)} alt="" />}
              <input ref={fileRef} type="file" accept="image/png,image/jpeg" hidden onChange={async (e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (!f) return;
                if (f.size > 2 * 1024 * 1024) { setLogoError("Up to 2 MB."); return; }
                setUploading(true);
                setLogoError(null);
                try {
                  set({ logo: await uploadLogo(f) });
                } catch (err) {
                  setLogoError(err instanceof Error ? err.message : "Couldn't upload the logo.");
                } finally {
                  setUploading(false);
                }
              }} />
              <button type="button" className="secondary" disabled={disabled || uploading} onClick={() => fileRef.current?.click()}>{uploading ? "Uploading…" : b.logo ? "Replace" : "Upload"}</button>
              {b.logo && <button type="button" className="link small" disabled={disabled} onClick={() => set({ logo: "" })}>Remove</button>}
            </div>
            {logoError && <span className="notice-text small">{logoError}</span>}
          </div>
        </div>
        {warnings.map((w) => <p key={w} className="notice-text small" style={{ margin: ".2rem 0" }}>{w}</p>)}
      </div>

      <div className="brand-preview" aria-label="Preview" style={{ background: r.background, color: r.text, fontFamily: r.fontStack }}>
        {b.logo && <img src={logoURL(b.logo)} alt="" style={{ maxHeight: 34, maxWidth: 120, objectFit: "contain", marginBottom: 8 }} />}
        <div style={{ fontWeight: 600, fontSize: 17, marginBottom: 8 }}>Book a meeting</div>
        <div style={{ background: r.surface, border: `1px solid ${r.line}`, borderRadius: r.radiusLg, padding: 12 }}>
          {["Introductory call", "Portfolio review"].map((s, i) => (
            <div key={s} style={{ display: "flex", justifyContent: "space-between", border: `1px solid ${i === 0 ? r.primary : r.line}`, borderRadius: r.radius, padding: "8px 10px", marginBottom: 8, fontSize: 13 }}>
              <span>{s}<br /><small style={{ color: r.muted }}>30 min</small></span><b>25 BHD</b>
            </div>
          ))}
          <div style={{ background: r.primary, color: r.onPrimary, borderRadius: r.radius, padding: "8px 10px", textAlign: "center", fontWeight: 600, fontSize: 13 }}>Confirm booking</div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12, alignItems: "flex-end" }}>
          <div style={{ background: r.primary, color: r.onPrimary, borderRadius: r.radius, padding: "6px 10px", fontSize: 12 }}>Hi, do you have time on Sunday?</div>
          <div style={{ width: 36, height: 36, borderRadius: r.radiusLg, background: r.primary, color: r.onPrimary, display: "grid", placeItems: "center", flex: "none" }}><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M5 6h14v9H9.5L6 18.5V15H5z" /></svg></div>
        </div>
      </div>
    </div>
  );
}
